/**
 * StatsView — aggregate counts pulled from localStorage. Read-only view;
 * no state of its own beyond what's already persisted.
 */
import type { Locale, World } from './types';
import { getSolvedSet, getLearnedWordsByLocale } from './storage';

interface Props {
  readonly locale: Locale;
  readonly worlds: readonly World[];
  readonly onBackToWorlds: () => void;
  readonly onOpenSettings: () => void;
}

const LOCALES: readonly Locale[] = ['en', 'fr', 'de'];

export function StatsView(props: Props) {
  const { locale, worlds, onBackToWorlds, onOpenSettings } = props;
  const solved = getSolvedSet();
  const learnedByLocale = getLearnedWordsByLocale();

  const totalSolved = solved.size;
  const totalLearned = Object.values(learnedByLocale).reduce(
    (acc, arr) => acc + (arr?.length ?? 0),
    0
  );

  return (
    <section id="statsView" className="view">
      <header className="topbar">
        <button className="navBtn" onClick={onBackToWorlds}>← Worlds</button>
        <div className="worldName">Statistics</div>
        <button className="iconBtn ghost" aria-label="Settings" onClick={onOpenSettings}>⋯</button>
      </header>

      <div className="board" id="statsBody">
        <div className="statBlock">
          <div className="statTotal">{totalSolved}</div>
          <div className="statSub">
            ladders solved · {totalLearned} unique words seen
          </div>
        </div>

        {LOCALES.map((loc) => {
          const solvedHere = [...solved].filter((k) => k.startsWith(`${loc}|`)).length;
          const learnedHere = (learnedByLocale[loc] ?? []).length;
          if (solvedHere === 0 && learnedHere === 0) return null;
          return (
            <div key={loc} className="statBlock">
              <h3>{loc.toUpperCase()}</h3>
              <div className="statRow">
                <span className="name">Ladders solved</span>
                <span className="val">{solvedHere}</span>
              </div>
              <div className="statRow">
                <span className="name">Unique words seen</span>
                <span className="val">{learnedHere}</span>
              </div>
            </div>
          );
        })}

        {worlds.length > 0 && (
          <div className="statBlock">
            <h3>Worlds — {locale.toUpperCase()}</h3>
            {worlds.map((w) => {
              const solvedHere = w.ladders.reduce(
                (acc, _, i) => acc + (solved.has(`${locale}|${w.worldId}|${i}`) ? 1 : 0),
                0
              );
              return (
                <div key={w.worldId} className="statRow">
                  <span className="name">{w.themeName}</span>
                  <span className="val">{solvedHere} / {w.ladders.length}</span>
                </div>
              );
            })}
          </div>
        )}

        {totalSolved === 0 && (
          <div style={{
            color: 'var(--muted)', fontStyle: 'italic',
            textAlign: 'center', padding: '1rem 0',
          }}>
            No ladders solved yet. Pick a world to begin.
          </div>
        )}
      </div>
    </section>
  );
}
